




























































































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Debounce } from '@/decorators/debounce';
import { UNIT_SEARCH } from '@/queries/Unit';
import dayjs, { Dayjs } from 'dayjs';
import Decimal from 'decimal.js-light';
import { IRules } from '../UI/Form/ValidationModule';
import { validateMin, validateRequired } from '../UI/Form/Validations';
import ProductNewButton from '../Product/ProductNewButton.vue';
import { GQL_warehousePatchProductsSearch } from '@/queries/__generated__/warehousePatchProductsSearch';
import { generateUID } from '@/utils/utils';
import { GQL_createProduct } from '@/queries/__generated__/createProduct';
import formatDateTime from '@/filters/formatDateTime';
import UnitClosingDatesInfo from '@/components/Unit/UnitClosingDatesInfo.vue';
import gql from 'graphql-tag';
import { PRODUCT_SEARCH_LIMIT, activateLine, inactiveLines, linesToSave } from '@/app/WarehousePatch';

const WAREHOUSE_PATCH_PRODUCTS_SEARCH = gql`
  query warehousePatchProductsSearch(
    $limit: Int,
    $offset: Int,
    $orderBy: String,
    $orderDirection: String,
    $filters: ProductFilter
  ) {
    products(
      limit: $limit,
      offset: $offset,
      orderBy: $orderBy,
      orderDirection: $orderDirection,
      filters: $filters,
    ){
      id
      number
      name
      firstWarehousePatch {
        id
        date
      }
      productPrices {
        id,
        purchasePrice,
        validTo,
        validFrom,
      }
      supplierUnit {
        id
        name
      }
    }
  },
`;

type WarehousePatchFormObjectLine = {
  id: string | null;
  productId: string;
  supplierUnitId: string | null;
  supplierUnitName: string | null;
  quantity: Decimal;
  purchasePrice: Decimal;
  productNumber: string;
  productName: string;
  productFirstWarehousePatchId: string | null;
  productFirstWarehousePatchDate: string | null;
  mustBeCheckpoint: boolean;
  checkpoint: boolean;
  totalPrice: Decimal | null;
  _destroy: boolean;
  $_UID: string;
  // Set on the lines the product search adds, until the user activates them.
  inactive?: boolean;
};

export type WarehousePatchFormObject = {
  id: string | null;
  unitId: string;
  date: string;
  supplierUnitId: string | null;
  supplierUnitName: string | null;
  comment: string | null;
  createdBy: string | null;
  createdAt: Dayjs | null;
  warehousePatchLines: WarehousePatchFormObjectLine[];
}

@Component({
  components: {
    ProductNewButton,
    UnitClosingDatesInfo,
  },
})
export default class WarehousePatchForm extends Vue {
  @Prop({ required: true }) warehousePatch!: WarehousePatchFormObject;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop(Object) private mutation!: any;
  @Prop({type: Function }) variables!: Function

  private UNIT_SEARCH = UNIT_SEARCH;
  private today = this.$dayjs().format('YYYY-MM-DD');

  private validForm = true;
  private productSearch = '';
  private productSearchCapped = false;
  private PRODUCT_SEARCH_LIMIT = PRODUCT_SEARCH_LIMIT;

  private formatDateTime = formatDateTime;

  private rules: IRules = {
    date: [ {
      validation: validateRequired(),
      message: i18n => i18n.t('Error.required', { field: this.t_attr('WarehousePatch', 'date') }),
    } ] ,
    warehousePatchLines: {
      purchasePrice: [
        {
          validation: validateMin(0),
          message: i18n => i18n.t('Error.cannotBeNegative', { field: this.t_attr('WarehousePatchLine','purchasePrice') }),
        },
      ],
    },
  }

  @Watch('warehousePatch.supplierUnitId')
  onSupplierSelected(): void {
    this.notDeletedLines
      .filter(line => line.id === null && line.quantity.isZero())
      .forEach(line => this.removeLine(line));
    if (this.warehousePatch.supplierUnitId) this.loadLinesForSupplier();
  }

  @Watch('productSearch')
  @Debounce(300)
  searchProducts(): void {
    // The hits of the previous search go unless the user activated them.
    inactiveLines(this.notDeletedLines).forEach(line => this.removeLine(line));
    this.productSearchCapped = false;
    const term = this.productSearch;
    if (!term) return;

    this.$apollo.query<GQL_warehousePatchProductsSearch>({
      query: WAREHOUSE_PATCH_PRODUCTS_SEARCH,
      variables: {
        // One more than is shown, to know whether the result was cut off.
        limit: PRODUCT_SEARCH_LIMIT + 1,
        filters: { numberAndName: term, activeDate: this.warehousePatch.date },
      },
      fetchPolicy: 'no-cache',
    }).then(data => {
      if (term !== this.productSearch) return;

      this.productSearchCapped = data.data.products.length > PRODUCT_SEARCH_LIMIT;

      data.data.products
        .slice(0, PRODUCT_SEARCH_LIMIT)
        .filter(product => !this.notDeletedLines.some(line => line.productId === product.id))
        .forEach(product => {
          const productPrice = product.productPrices.find(price => {
            return dayjs
              .strict(this.warehousePatch.date)
              .isBetween(price.validFrom, price.validTo, 'date', '[]');
          });

          this.warehousePatch.warehousePatchLines.push(
            {
              id: null,
              productId: product.id,
              supplierUnitId: product.supplierUnit?.id || null,
              supplierUnitName: product.supplierUnit?.name || null,
              quantity: new Decimal(0),
              purchasePrice: productPrice?.purchasePrice || new Decimal(0),
              productNumber: product.number,
              productName: product.name,
              productFirstWarehousePatchId: product.firstWarehousePatch?.id || null,
              productFirstWarehousePatchDate: product.firstWarehousePatch?.date || null,
              checkpoint: false,
              mustBeCheckpoint: false,
              totalPrice: new Decimal(0),
              _destroy: false,
              $_UID: generateUID(),
              // Greyed out and disabled until the user activates it.
              inactive: true,
            },
          );
        });
        this.checkIfLinesMustBeCheckpoints();
    });
  }

  loadLinesForSupplier(): void {
    // If this is a normal patch, bound to one supplier, we will have supplierUnitId on the patch.
    // For checkpoint patches, it will not have a supplierUnitId on the patch, but only on the groups.
    // Button which triggers this can only be pressed if there is exactly one group.
    const supplierUnitId = this.warehousePatch.supplierUnitId || this.groupedLines[0].supplierUnitId;

    this.$apollo.query<GQL_warehousePatchProductsSearch>({
      query: WAREHOUSE_PATCH_PRODUCTS_SEARCH,
      // TODO revisit limit in case we have a need for more
      variables: { limit: 1000, filters: {supplierUnitId} },
      fetchPolicy: 'no-cache',
    }).then(data => {
      // A product the search put on the patch is now loaded from its
      // supplier, so its line is an ordinary one.
      const productIds = new Set(data.data.products.map(product => product.id));
      inactiveLines(this.notDeletedLines)
        .filter(line => productIds.has(line.productId))
        .forEach(line => activateLine(line));

      data.data.products
        .filter(product => !this.notDeletedLines.some(line => line.productId === product.id))
        .forEach(product => {
          const productPrice = product.productPrices.find(price => {
            return dayjs
              .strict(this.warehousePatch.date)
              .isBetween(price.validFrom, price.validTo, 'date', '[]');
          });

          this.warehousePatch.warehousePatchLines.push(
            {
              id: null,
              productId: product.id,
              supplierUnitId: product.supplierUnit!.id,
              supplierUnitName: product.supplierUnit!.name,
              quantity: new Decimal(0),
              purchasePrice: productPrice?.purchasePrice || new Decimal(0),
              productNumber: product.number,
              productName: product.name,
              productFirstWarehousePatchId: product.firstWarehousePatch?.id || null,
              productFirstWarehousePatchDate: product.firstWarehousePatch?.date || null,
              checkpoint: false,
              mustBeCheckpoint: false, // this will be calculated in checkIfLinesMustBeCheckpoints function
              totalPrice: new Decimal(0),
              _destroy: false,
              $_UID: generateUID(),
            },
          );
        });
        this.checkIfLinesMustBeCheckpoints();
    });
  }

  private loadLineForNewProduct({data}: { data: GQL_createProduct }): void {
    const product = data.createProduct?.product;
    if (!product) return;
    if (this.warehousePatch.supplierUnitId && product.supplierUnit?.id != this.warehousePatch.supplierUnitId) return;

    const productPrice = product.productPrices.find(price => {
      return dayjs
        .strict(this.warehousePatch.date)
        .isBetween(price.validFrom, price.validTo, 'date', '[]');
    });

    this.warehousePatch.warehousePatchLines.push(
      {
        id: null,
        productId: product.id,
        supplierUnitId: product.supplierUnit?.id || null,
        supplierUnitName: product.supplierUnit?.name || null,
        quantity: new Decimal(0),
        purchasePrice: productPrice?.purchasePrice || new Decimal(0),
        productNumber: product.number,
        productName: product.name,
        productFirstWarehousePatchId: null,
        productFirstWarehousePatchDate: null,
        checkpoint: true,
        mustBeCheckpoint: true,
        totalPrice: new Decimal(0),
        _destroy: false,
        $_UID: generateUID(),
      },
    );
  }

  @Watch('warehousePatch')
  @Watch('warehousePatch.date', { immediate: true })
  private checkIfLinesMustBeCheckpoints() {
    const warehousePatchDate = dayjs.strict(this.warehousePatch.date);

    this.warehousePatch.warehousePatchLines.forEach(line => {
      line.mustBeCheckpoint = !!(
        line.productFirstWarehousePatchDate === null ||
        !this.warehousePatch.id && warehousePatchDate.isBefore(line.productFirstWarehousePatchDate) ||
        this.warehousePatch.id && this.warehousePatch.id == line.productFirstWarehousePatchId ||
        this.warehousePatch.id && this.warehousePatch.id != line.productFirstWarehousePatchId && warehousePatchDate.isBefore(line.productFirstWarehousePatchDate)
      );

      if (line.mustBeCheckpoint) {
        line.checkpoint = true;
      }
    });
  }

  private lineSum(line: WarehousePatchFormObjectLine) {
    return line.quantity.times(line.purchasePrice);
  }

  get totalSum() {
    return this.notDeletedLines.reduce((sum, line) => sum.plus(this.lineSum(line)), new Decimal(0));
  }

  private warehousePatchSaved({ data }: { data: object }) {
    this.$sMessage.success('Successfully saved.');
    this.$emit('done', data);
  }

  private activateLine(line: WarehousePatchFormObjectLine) {
    activateLine(line);
  }

  private removeLine(line: WarehousePatchFormObjectLine) {
    if (line.id) {
      Vue.set(line, '_destroy', true);
    } else {
      this.warehousePatch.warehousePatchLines.splice(
        this.warehousePatch.warehousePatchLines.indexOf(line),
        1,
      );
    }
  }

  // Lines that are saved: inactive lines and the ones that affect nothing are dropped on save.
  private get keptLinesCount(): number {
    return linesToSave(this.warehousePatch.warehousePatchLines).filter(line => !line._destroy).length;
  }

  private get notDeletedLines() {
    return this
      .warehousePatch
      .warehousePatchLines
      .filter((warehousePatchLine) => !warehousePatchLine._destroy);
  }

  private get groupedLines() {
    const grouped: Map<
      string | null,
      {
        supplierUnitId: string;
        supplierUnitName: string;
        lines: WarehousePatchFormObjectLine[];
      }
    > = new Map();

    this.notDeletedLines.forEach(line => {
      const key = line.supplierUnitId || null;

      if (!grouped.has(key)) {
        grouped.set(key, {
          supplierUnitId: line.supplierUnitId || '',
          supplierUnitName: line.supplierUnitName || this.$t('WarehousePatch.noSupplier').toString(),
          lines: [],
        });
      }

      grouped.get(key)!.lines.push(line);
    });

    return Array.from(grouped.values());
  }
}
